import { useState, useRef, useCallback, useEffect } from 'react';

export const useWordByWordAnimation = () => {
  const [displayedText, setDisplayedText] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const wordBufferRef = useRef('');
  const wordsToDisplayRef = useRef<string[]>([]);
  const currentWordIndexRef = useRef(0);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isStreamingRef = useRef(false);
  const shouldStopAnimationRef = useRef(false);

  const extractCompleteWords = useCallback((text: string): string[] => {
    const words: string[] = [];
    let currentWord = '';
    let inHtmlTag = false;

    for (const char of text) {
      if (char === '<') {
        inHtmlTag = true;
      }
      currentWord += char;
      if (char === '>') {
        inHtmlTag = false;
        words.push(currentWord);
        currentWord = '';
        continue;
      }
      if (!inHtmlTag && /\s/.test(char)) {
        words.push(currentWord);
        currentWord = '';
      }
    }
    if (currentWord) {
      words.push(currentWord);
    }
    return words;
  }, []);

  const animateWords = useCallback(() => {
    if (shouldStopAnimationRef.current) {
      setIsAnimating(false);
      return;
    }

    const words = wordsToDisplayRef.current;
    const currentIndex = currentWordIndexRef.current;

    if (currentIndex >= words.length) {
      if (isStreamingRef.current) {
        animationTimeoutRef.current = setTimeout(animateWords, 10);
        return;
      }
      setIsAnimating(false);
      return;
    }

    setDisplayedText(words.slice(0, currentIndex + 1).join(''));
    currentWordIndexRef.current++;
    animationTimeoutRef.current = setTimeout(animateWords, 10);
  }, []);

  const startAnimation = useCallback(() => {
    if (animationTimeoutRef.current) {
      return;
    }
    shouldStopAnimationRef.current = false;
    setIsAnimating(true);
    setDisplayedText('');
    currentWordIndexRef.current = 0;
    animateWords();
  }, [animateWords]);

  const stopAnimation = useCallback(() => {
    shouldStopAnimationRef.current = true;
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }
    setIsAnimating(false);
  }, []);

  const appendText = useCallback(
    (chunk: string) => {
      isStreamingRef.current = true;
      wordBufferRef.current += chunk;
      const allWords = extractCompleteWords(wordBufferRef.current);

      if (allWords.length > 1) {
        const completeWords = allWords.slice(0, -1);
        const incompleteWord = allWords[allWords.length - 1];
        wordsToDisplayRef.current = [
          ...wordsToDisplayRef.current,
          ...completeWords,
        ];
        wordBufferRef.current = incompleteWord;
        if (!animationTimeoutRef.current) {
          startAnimation();
        }
      }
    },
    [extractCompleteWords, startAnimation]
  );

  const setText = useCallback(
    (text: string) => {
      isStreamingRef.current = false;
      const words = extractCompleteWords(text);
      wordBufferRef.current = '';
      wordsToDisplayRef.current = words;
      if (!animationTimeoutRef.current) {
        startAnimation();
      }
    },
    [extractCompleteWords, startAnimation]
  );

  const finishStreaming = useCallback(() => {
    isStreamingRef.current = false;
    if (wordBufferRef.current.trim()) {
      wordsToDisplayRef.current = [
        ...wordsToDisplayRef.current,
        wordBufferRef.current,
      ];
      wordBufferRef.current = '';
    }
  }, []);

  const reset = useCallback((isError?: boolean) => {
    stopAnimation();
    wordBufferRef.current = '';
    wordsToDisplayRef.current = [];
    currentWordIndexRef.current = 0;
    isStreamingRef.current = false;
    setDisplayedText(isError ? '' : null);
  }, [stopAnimation]);

  useEffect(
    () => () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    },
    []
  );

  return {
    displayedText,
    isAnimating,
    appendText,
    setText,
    finishStreaming,
    reset,
  };
};
